import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {User} from '../types';
import {authService, settingsService} from '../services';
import {clearFrameCache} from '../services/offlineCache';
import {NetworkError, probeConnectivity} from '../services/api';
import {storage} from '../services/storage';
import i18n from '../i18n';

const USER_CACHE_KEY = 'oma_cached_user';

// Keys that contain user-specific data and must be cleared on logout
const USER_DATA_KEYS = [
  'oma_last_viewed_display_id',
  'oma_frame_current_photo_index',
  'homeBackgroundPhotoId',
  'homeBackgroundTimestamp',
];

/**
 * Clear user-specific storage keys that could leak data between accounts.
 * Called from both explicit logout and 401 auto-logout.
 */
export async function clearUserData(): Promise<void> {
  await Promise.all(
    USER_DATA_KEYS.map(key => storage.removeItem(key).catch(() => {})),
  );
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,
};

export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, {rejectWithValue}) => {
    try {
      // Run all three checks in parallel to minimize cold-start time
      const [hasSession, online, cachedUser] = await Promise.all([
        authService.hasSession(),
        probeConnectivity(),
        loadCachedUser(),
      ]);

      if (!hasSession) {
        return {user: null, isAuthenticated: false};
      }

      // Offline: skip network calls, use cached user immediately
      if (!online) {
        return {user: cachedUser, isAuthenticated: true};
      }

      const response = await authService.validateSession();
      if (response.valid && response.user) {
        // Cache user for offline access
        await storage.setItem(USER_CACHE_KEY, JSON.stringify(response.user));
        // Load user settings and apply locale
        try {
          const settings = await settingsService.getSettings();
          if (settings.locale) {
            await i18n.changeLanguage(settings.locale);
          }
        } catch (e) {
          // Settings load failed, continue with default locale
        }
        return {user: response.user, isAuthenticated: true};
      }

      return {user: null, isAuthenticated: false};
    } catch (error) {
      if (error instanceof NetworkError) {
        // Network failure — trust stored token if it exists
        const hasToken = await authService.hasSession();
        if (hasToken) {
          const cachedUser = await loadCachedUser();
          return {user: cachedUser, isAuthenticated: true};
        }
      }
      // Server error or no token → not authenticated
      return {user: null, isAuthenticated: false};
    }
  },
);

async function loadCachedUser(): Promise<User | null> {
  try {
    const data = await storage.getItem(USER_CACHE_KEY);
    if (data) {
      const parsed = JSON.parse(data);
      if (parsed && parsed.id) {
        return parsed;
      }
    }
  } catch {}
  return null;
}

export const createAccount = createAsyncThunk(
  'auth/createAccount',
  async (deviceInfo: string | undefined, {rejectWithValue}) => {
    try {
      // Clear any cached data from previous user
      await clearFrameCache();
      const response = await authService.createAccount({deviceInfo});
      await storage.setItem(USER_CACHE_KEY, JSON.stringify(response.user));
      return response.user;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create account');
    }
  },
);

export const loginWithQr = createAsyncThunk(
  'auth/loginWithQr',
  async (
    {qrToken, deviceInfo}: {qrToken: string; deviceInfo?: string},
    {rejectWithValue},
  ) => {
    try {
      // Clear any cached data from previous user
      await clearFrameCache();
      const response = await authService.loginWithQr({qrToken, deviceInfo});
      await storage.setItem(USER_CACHE_KEY, JSON.stringify(response.user));
      return response.user;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Invalid QR code');
    }
  },
);

export const verifyMagicLink = createAsyncThunk(
  'auth/verifyMagicLink',
  async (
    {token, deviceInfo}: {token: string; deviceInfo?: string},
    {rejectWithValue},
  ) => {
    try {
      // Clear any cached data from previous user
      await clearFrameCache();
      const response = await authService.verifyMagicLink({token, deviceInfo});
      await storage.setItem(USER_CACHE_KEY, JSON.stringify(response.user));
      return response.user;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Invalid or expired link');
    }
  },
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await authService.logout();
  // Clear cached photos, user cache, user-specific data, and reset language on logout
  await clearFrameCache();
  await storage.removeItem(USER_CACHE_KEY);
  await clearUserData();
  await i18n.changeLanguage('en');
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    clearAuth: state => {
      // Clear auth state without calling backend (for use after account deletion)
      state.user = null;
      state.isAuthenticated = false;
    },
  },
  extraReducers: builder => {
    // Initialize auth
    builder.addCase(initializeAuth.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(initializeAuth.fulfilled, (state, action) => {
      state.user = action.payload.user;
      state.isAuthenticated = action.payload.isAuthenticated;
      state.isLoading = false;
      state.isInitialized = true;
    });
    builder.addCase(initializeAuth.rejected, state => {
      state.isLoading = false;
      state.isInitialized = true;
    });

    // Create account
    builder.addCase(createAccount.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createAccount.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isLoading = false;
    });
    builder.addCase(createAccount.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Login with QR
    builder.addCase(loginWithQr.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(loginWithQr.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isLoading = false;
    });
    builder.addCase(loginWithQr.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Verify magic link
    builder.addCase(verifyMagicLink.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(verifyMagicLink.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isLoading = false;
    });
    builder.addCase(verifyMagicLink.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Logout
    builder.addCase(logout.fulfilled, state => {
      state.user = null;
      state.isAuthenticated = false;
    });
  },
});

export const {clearError, setUser, clearAuth} = authSlice.actions;
export default authSlice.reducer;
